import React, { useEffect, useMemo, useRef } from "react";
import { FormDemo } from "./pages/FormDemo";
import { Components } from "./pages/Components";
import { Login } from "./pages/Login";
import { Dashboard } from "./pages/Dashboard";
import { SideMenu } from "./components/FormFields/Layout/SideMenu";
import { Header } from "./components/FormFields/Layout/Header";
import { ComponentRenderer } from "./components/ComponentRenderer";
import { PDFViewer } from "./components/FormFields/PDFViewer";
import { HTMLDrawer } from "./components/FormFields/HTMLDrawer";
import { ConfirmationDialog } from "./components/FormFields/ConfirmationDialog";
import { Chatbot } from "./components/Chatbot";
import { AppTour } from "./components/AppTour";
import { useAppDispatch, useAppSelector } from "./state/hooks";
import { useDarkMode } from "./contexts/DarkModeContext";
import { useTour } from "./contexts/TourContext";
import { useAnnouncement } from "./contexts/AnnouncementContext";
import { useNotification } from "./contexts/NotificationContext";
import { useWhatsNew } from "./contexts/WhatsNewContext";
import loaderLight from "../public/images/loader_light.gif";
import loaderDark from "../public/images/loader_dark.gif";
import {
  initializeAuth,
  loginWithEncryptedData,
  logout,
  updateUserBranch,
} from "./state/slices/authSlice";
import {
  setCurrentBranch,
  setAvailableBranches,
  setViewportMode,
  toggleSideMenu,
} from "./state/slices/layoutSlice";
import { apiService } from "./services/api";
import { logoutByUserId, validateSessionId } from "./services/sessionApi";
import type { Branch } from "./types/user";
import { updateScreenFormField } from "./state/slices/formsSlice";
import { executeConfigAction } from "./state/thunks/configThunks";
import {
  deriveScreenId,
  extractDrawerMenu,
  getRecordIdFromView,
  screenConfigRegistry,
} from "./state/utils/configUtils";
import { navigateToView, clearHistory } from "./state/slices/navigationSlice";
import {
  clearNotification,
  showNotification,
} from "./state/slices/notificationsSlice";
import { closePDFViewer } from "./state/slices/pdfViewerSlice";
import {
  selectIsAuthenticated,
  selectUser,
  selectLayout,
  selectNavigation,
  selectCurrentView,
  selectFormDataByScreen,
  selectScreenLoading,
  selectCurrentNotification,
  selectCurrentBranch,
} from "./state/selectors";
import { fetchSettings, selectShowNewUpdatesNotification, selectShowSystemNotifications } from "./state/slices/settingsSlice";
import { useSessionMonitor } from "./hooks/useSessionMonitor";
import { usePermissions } from "./hooks/usePermissions";
import type { DrawerMenuItem, DrawerSubmenuItem } from "./services/types/ComponentTypes";
import { ScreenRenderer } from "./components/ScreenRenderer";
import { ScreenPopupModal } from "./components/ScreenPopupModal";

// Feature flags for optional UX elements
const ENABLE_ANNOUNCEMENTS = false;
const ENABLE_AUTO_TOUR_START = false;
const ENABLE_ANNOUNCEMENT_TOUR_TRIGGER = false;

const App: React.FC = () => {
  const dispatch = useAppDispatch();
  const { isDarkMode } = useDarkMode();
  const { startTour, hasCompletedTour, setNavigationCallback, endTour, markTourComplete } = useTour();
  const { fetchAnnouncements, setNavigationCallback: setAnnouncementNavCallback, setUserName: setAnnouncementUserName } = useAnnouncement();
  const announcementDismissedRef = useRef(false);
  const { fetchNotifications, setNavigationCallback: setNotificationNavCallback } = useNotification();
  const { fetchUpdates: fetchWhatsNew, setNavigationCallback: setWhatsNewNavCallback } = useWhatsNew();

  const isAuthenticated = useAppSelector(selectIsAuthenticated);
  const user = useAppSelector(selectUser);
  const layout = useAppSelector(selectLayout);
  const navigation = useAppSelector(selectNavigation);
  const notification = useAppSelector(selectCurrentNotification);
  const currentView = useAppSelector(selectCurrentView);
  const formsState = useAppSelector((state) => state.forms);
  const showNewUpdatesNotification = useAppSelector(selectShowNewUpdatesNotification);
  const showSystemNotifications = useAppSelector(selectShowSystemNotifications);
  const settingsState = useAppSelector((state) => state.settings);

  // Debug: Log notification visibility flags
  // useEffect(() => {
  //   console.log("[App] Notification visibility flags:", {
  //     showNewUpdatesNotification,
  //     showSystemNotifications,
  //     settings: settingsState.settings,
  //     loading: settingsState.loading,
  //   });
  // }, [showNewUpdatesNotification, showSystemNotifications, settingsState.settings, settingsState.loading]);

  useEffect(() => {
    dispatch(initializeAuth());
    
  }, [dispatch]);

  // Validate session_id on app initialization (page refresh)
  useEffect(() => {
    const validateSessionOnInit = async () => {
      const sessionId = localStorage.getItem("session_id");
      if (sessionId && isAuthenticated) {
        try {
          const result = await validateSessionId(sessionId);
          if (result && !result.valid) {
            // Session invalid - force logout
            console.warn("[App] Session invalid on init - logging out");
            dispatch(
              showNotification({
                message:
                  "You have been logged out because you logged in from another system.",
                type: "error",
              })
            );
            dispatch(logout());
            dispatch(clearHistory());
            dispatch(navigateToView({ view: "dashboard" }));
          }
        } catch (error) {
          console.warn("[App] Failed to validate session on init:", error);
        }
      }
    };

    if (isAuthenticated) {
      validateSessionOnInit();
    }
  }, [dispatch, isAuthenticated]);

  // Monitor session status - check periodically and on focus
  useSessionMonitor();

  // Fetch settings when authenticated
  useEffect(() => {
    if (isAuthenticated) {
      dispatch(fetchSettings());
    }
  }, [dispatch, isAuthenticated]);

  // Job Card add (156): seed or correct parentid from URL so Report/SubModules use estimation id
  const derivedScreenIdForEffect = deriveScreenId(currentView);
  const idFromView = getRecordIdFromView(currentView);
  const formData156 = formsState.formDataByScreen["156"] || {};
  useEffect(() => {
    if (derivedScreenIdForEffect !== "156" || !idFromView) return;
    const currentParent = formData156.parentid ?? formData156.parent_id ?? "";
    const currentStr = String(currentParent).trim();
    const idFromViewStr = String(idFromView).trim();
    if (!currentStr || currentStr !== idFromViewStr) {
      dispatch(updateScreenFormField({ screenId: "156", name: "parentid", value: idFromView }));
      dispatch(updateScreenFormField({ screenId: "156", name: "parent_id", value: idFromView }));
    }
  }, [currentView, idFromView, derivedScreenIdForEffect, dispatch, formData156.parentid, formData156.parent_id]);

  useEffect(() => {
    const checkScreenSize = () => {
      const width = window.innerWidth;
      const mobile = width < 768;
      const tablet = width >= 768 && width < 1024;
      dispatch(setViewportMode({ isMobile: mobile, isTablet: tablet }));
    };

    checkScreenSize();
    window.addEventListener("resize", checkScreenSize);
    return () => window.removeEventListener("resize", checkScreenSize);
  }, [dispatch]);

  const { hasPermission, hasModulePermission } = usePermissions();

  const drawerMenuConfig = useMemo(() => {
    const config = extractDrawerMenu();
    if (!config) return config;

    const filterMenuItems = (items: DrawerMenuItem[]) => {
      return items.filter(item => {
        // Module level permission check
        if (item.moduleId && !hasModulePermission(item.moduleId)) {
          return false;
        }

        // Screen level permission check
        if (item.screenId && !hasPermission(item.screenId, "Unlock")) {
          return false;
        }

        return true;
      }).map(item => {
        // If it has a submenu, filter its items recursively
        if (item.submenu && item.submenu.items) {
          const filteredSubmenuItems = item.submenu.items.filter((subitem: DrawerSubmenuItem) => {
            if (subitem.screenId && !hasPermission(subitem.screenId, "Unlock")) {
              return false;
            }
            return true;
          });

          return {
            ...item,
            submenu: {
              ...item.submenu,
              items: filteredSubmenuItems
            }
          };
        }
        return item;
      });
    };

    return {
      ...config,
      menu: filterMenuItems(config.menu)
    };
  }, [hasPermission, hasModulePermission]);

  const pdfViewerState = useAppSelector((state) => state.pdfViewer);
  const { toggleDarkMode } = useDarkMode();

  // Keyboard shortcuts handler
  useEffect(() => {
    if (!isAuthenticated) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      // Prevent shortcuts when typing in inputs, textareas, or selects
      const target = e.target as HTMLElement;
      if (
        target instanceof HTMLInputElement ||
        target instanceof HTMLTextAreaElement ||
        target instanceof HTMLSelectElement ||
        target.isContentEditable ||
        target.closest('[contenteditable="true"]')
      ) {
        // Allow Esc to work even in inputs
        if (e.key === "Escape") {
          // Close PDF viewer if open
          if (pdfViewerState.isOpen) {
            e.preventDefault();
            dispatch(closePDFViewer());
            return;
          }
        }
        return;
      }

      // Ctrl + S: Save
      if (e.ctrlKey && e.key === "s" && !e.shiftKey) {
        e.preventDefault();
        const submitButton = document.querySelector('[data-submit-button="true"]:not([disabled])') as HTMLButtonElement;
        if (submitButton) {
          submitButton.click();
        }
        return;
      }

      // Ctrl + Shift + S: Save & Close
      if (e.ctrlKey && e.shiftKey && e.key === "S") {
        e.preventDefault();
        const submitButton = document.querySelector('[data-submit-button="true"]:not([disabled])') as HTMLButtonElement;
        if (submitButton) {
          submitButton.click();
          // Navigate back after a short delay to allow save to complete
          setTimeout(() => {
            if (navigation.previousView) {
              dispatch(navigateToView({ view: navigation.previousView, isBack: true }));
            }
          }, 300);
        }
        return;
      }

      // Esc: Close popup/Cancel
      if (e.key === "Escape") {
        // Close PDF viewer if open
        if (pdfViewerState.isOpen) {
          e.preventDefault();
          dispatch(closePDFViewer());
          return;
        }
        // Trigger cancel action
        const cancelButton = document.querySelector('[data-cancel-button="true"]') as HTMLButtonElement;
        if (cancelButton) {
          cancelButton.click();
          return;
        }
        // Navigate back if no cancel button
        if (navigation.previousView && currentView !== "dashboard" && currentView !== "home") {
          dispatch(navigateToView({ view: navigation.previousView, isBack: true }));
        }
        return;
      }

      // Ctrl + R: Refresh
      if (e.ctrlKey && e.key === "r" && !e.shiftKey) {
        e.preventDefault();
        const refreshButton = document.querySelector('[data-refresh-button="true"]') as HTMLButtonElement;
        if (refreshButton) {
          refreshButton.click();
        } else {
          // Fallback: reload page if no refresh button found
          window.location.reload();
        }
        return;
      }

      // Ctrl + Shift + D: Toggle Dark Mode
      if (e.ctrlKey && e.shiftKey && e.key === "D") {
        e.preventDefault();
        toggleDarkMode();
        return;
      }

      // Ctrl + P: Print/PDF
      if (e.ctrlKey && e.key === "p" && !e.shiftKey) {
        e.preventDefault();
        // Try to find PDF button in current context
        const pdfButton = document.querySelector('[data-pdf-button="true"]:not([disabled])') as HTMLButtonElement;
        if (pdfButton) {
          pdfButton.click();
        }
        return;
      }

      // Ctrl + Shift + P: PDF Export (same as Ctrl + P for now)
      if (e.ctrlKey && e.shiftKey && e.key === "P") {
        e.preventDefault();
        const pdfButton = document.querySelector('[data-pdf-button="true"]:not([disabled])') as HTMLButtonElement;
        if (pdfButton) {
          pdfButton.click();
        }
        return;
      }

      // Ctrl + E: Edit
      if (e.ctrlKey && e.key === "e" && !e.shiftKey) {
        e.preventDefault();
        const editButton = document.querySelector('[data-edit-button="true"]:not([disabled])') as HTMLButtonElement;
        if (editButton) {
          editButton.click();
        }
        return;
      }

      // Ctrl + D: Delete
      if (e.ctrlKey && e.key === "d" && !e.shiftKey) {
        e.preventDefault();
        const deleteButton = document.querySelector('[data-delete-button="true"]:not([disabled])') as HTMLButtonElement;
        if (deleteButton) {
          deleteButton.click();
        }
        return;
      }

      // Ctrl + A: Add (FAB button)
      if (e.ctrlKey && e.key === "a" && !e.shiftKey) {
        e.preventDefault();
        const fabButton = document.querySelector('[data-fab-button="true"]:not([disabled])') as HTMLButtonElement;
        if (fabButton) {
          fabButton.click();
        }
        return;
      }

      // F1: Inspection
      if (e.key === "F1") {
        e.preventDefault();
        dispatch(navigateToView({ view: "103", rememberPrevious: true }));
        return;
      }

      // F2: Job Card Summary List
      if (e.key === "F2") {
        e.preventDefault();
        dispatch(navigateToView({ view: "104", rememberPrevious: true }));
        return;
      }

      // F3: Add Customer
      if (e.key === "F3") {
        e.preventDefault();
        dispatch(navigateToView({ view: "123", rememberPrevious: true }));
        return;
      }

      // F4: Add Vehicle
      if (e.key === "F4") {
        e.preventDefault();
        dispatch(navigateToView({ view: "102", rememberPrevious: true }));
        return;
      }

      // F5: Add Products
      if (e.key === "F5") {
        e.preventDefault();
        dispatch(navigateToView({ view: "106", rememberPrevious: true }));
        return;
      }

      // F6: Add Labour / Others
      if (e.key === "F6") {
        e.preventDefault();
        dispatch(navigateToView({ view: "119", rememberPrevious: true }));
        return;
      }

      // F7: Add Vendor
      if (e.key === "F7") {
        e.preventDefault();
        dispatch(navigateToView({ view: "128", rememberPrevious: true }));
        return;
      }

      // Ctrl + J: Job Card List
      if (e.ctrlKey && e.key === "j" && !e.shiftKey) {
        e.preventDefault();
        dispatch(navigateToView({ view: "104", rememberPrevious: true }));
        return;
      }

      // Ctrl + Shift + J: New Job Card
      if (e.ctrlKey && e.shiftKey && e.key === "J") {
        e.preventDefault();
        dispatch(navigateToView({ view: "modulev1/jobcard_add", rememberPrevious: true }));
        return;
      }

      // Ctrl + H: Navigate to Home
      if (e.ctrlKey && e.key === "h" && !e.shiftKey) {
        e.preventDefault();
        dispatch(navigateToView({ view: "dashboard" }));
        return;
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [isAuthenticated, pdfViewerState.isOpen, navigation.previousView, currentView, dispatch, toggleDarkMode]);

  // Register navigation callback for the tour and (optionally) announcements
  useEffect(() => {
    if (!isAuthenticated) return;

    const navigationHandler = (screenId: string) => {
      // Navigate to the specified screen during tour or from announcements
      dispatch(navigateToView({ view: screenId }));
    };

    // Register for tour (always enabled)
    setNavigationCallback(navigationHandler);

    if (ENABLE_ANNOUNCEMENTS) {
      // Register for announcements
      setAnnouncementNavCallback(navigationHandler);

      // Register for notifications
      setNotificationNavCallback(navigationHandler);

      // Register for what's new
      setWhatsNewNavCallback(navigationHandler);
    }
  }, [isAuthenticated, setNavigationCallback, setAnnouncementNavCallback, setNotificationNavCallback, setWhatsNewNavCallback, dispatch]);

  // Set user name for personalized announcements and fetch announcements/notifications
  useEffect(() => {
    if (!ENABLE_ANNOUNCEMENTS) return;
    if (isAuthenticated && user) {
      // Set user name for personalized greetings
      if (user.name) {
        // Extract first name if full name is provided
        const firstName = user.name.split(" ")[0];
        setAnnouncementUserName(firstName);
      }

      // Fetch announcements, notifications, and what's new after a small delay to let the app render
      const timer = setTimeout(() => {
        fetchAnnouncements();
        fetchNotifications();
        fetchWhatsNew();
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [isAuthenticated, user, fetchAnnouncements, fetchNotifications, fetchWhatsNew, setAnnouncementUserName]);

  // Track if tour is being intentionally started
  const tourStartingRef = useRef(false);

  // Listen for start-tour event from announcements (optional)
  useEffect(() => {
    if (!ENABLE_ANNOUNCEMENT_TOUR_TRIGGER) return;

    const handleStartTour = () => {
      tourStartingRef.current = true;
      startTour();
      // Reset flag after tour has started
      setTimeout(() => {
        tourStartingRef.current = false;
      }, 1000);
    };

    window.addEventListener("start-app-tour", handleStartTour);
    return () => window.removeEventListener("start-app-tour", handleStartTour);
  }, [startTour]);

  // Listen for announcement dismissal to prevent auto-start tour
  useEffect(() => {
    const handleAnnouncementDismissed = () => {
      announcementDismissedRef.current = true;
      // Only close tour if it's not being intentionally started
      // Check both the ref and the window flag (set before modal closes)
      const isTourStarting = tourStartingRef.current || (window as any).__tourStarting === true;
      if (!isTourStarting) {
        endTour();
        // Mark tour as complete (same as "Skip Tour") so it doesn't show again after refresh
        markTourComplete();
      }
    };

    window.addEventListener("announcement-dismissed", handleAnnouncementDismissed);
    return () => window.removeEventListener("announcement-dismissed", handleAnnouncementDismissed);
  }, [endTour, markTourComplete]);

  // Auto-start tour for new users (first-time login) - disabled by default
  useEffect(() => {
    if (!ENABLE_AUTO_TOUR_START) return;
    if (isAuthenticated && !hasCompletedTour) {
      // Small delay to ensure the app is fully rendered
      const tourTimer = setTimeout(() => {
        // Check if announcement was dismissed before starting tour
        if (!announcementDismissedRef.current) {
          startTour();
        }
      }, 1500);
      return () => clearTimeout(tourTimer);
    }
  }, [isAuthenticated, hasCompletedTour, startTour]);

  // Pre-populate current branch from logged-in user and fetch all available branches
  useEffect(() => {
    if (isAuthenticated && user) {
      // Step 1: Always pre-populate current branch from logged-in user data
      const userBranch: Branch | null = user.branch_id && user.branch_name
        ? {
          id: user.branch_id,
          name: user.branch_name,
        }
        : null;

      if (userBranch) {
        //console.log("[App] Pre-populating current branch from user:", userBranch);
        dispatch(setCurrentBranch(userBranch));
      }

      // Step 2: If user has branch_switching permission, fetch ALL available branches
      if (user.branch_switching === 1) {
        console.log("[App] User has branch switching permission, fetching all branches...");

        apiService.getUserBranches().then((branches) => {
          console.log("[App] Fetched branches from API:", branches);

          let allBranches: Branch[] = [];

          if (branches && branches.length > 0) {
            // Filter out invalid branches and ensure all have id and name
            allBranches = branches
              .filter((b: any) => b && b.id && b.name)
              .map((b: any) => ({
                id: String(b.id),
                name: String(b.name),
              }));

            console.log("[App] Valid branches after filtering:", allBranches);
          }

          // Ensure current user's branch is included in the list if not already there
          if (userBranch) {
            const userBranchExists = allBranches.some((b) => b.id === userBranch.id);
            if (!userBranchExists) {
              console.log("[App] Adding user branch to available branches list");
              allBranches.unshift(userBranch); // Add at the beginning
            }
          }

          // Set all available branches for dropdown
          if (allBranches.length > 0) {
            console.log("[App] Setting available branches:", allBranches);
            dispatch(setAvailableBranches(allBranches));

            // Ensure current branch is set (use the one from API if it matches, otherwise use user branch)
            if (userBranch) {
              const matchedBranch = allBranches.find((b) => b.id === userBranch.id) || userBranch;
              console.log("[App] Setting current branch to:", matchedBranch);
              dispatch(setCurrentBranch(matchedBranch));
            }
          } else {
            // If API returned no branches, at least show the user's current branch
            console.warn("[App] No branches returned from API, using only user branch");
            if (userBranch) {
              dispatch(setAvailableBranches([userBranch]));
              dispatch(setCurrentBranch(userBranch));
            }
          }
        }).catch((error) => {
          console.error("[App] Error fetching branches from API:", error);
          // Fallback: if API fails, at least show the user's current branch
          if (userBranch) {
            console.log("[App] Using user branch as fallback due to API error");
            dispatch(setAvailableBranches([userBranch]));
            dispatch(setCurrentBranch(userBranch));
          }
        });
      } else {
        // User doesn't have branch switching permission - only show current branch
        // console.log("[App] User does not have branch switching permission");
        if (userBranch) {
          dispatch(setAvailableBranches([userBranch]));
        }
      }
    }
  }, [isAuthenticated, user, dispatch]);

  const handleLogin = (encryptedData: string) => {
    dispatch(
      loginWithEncryptedData({
        encryptedData,
        fallbackBranch: layout.currentBranch?.name || undefined,
      })
    ).then(() => {
      dispatch(clearHistory());
      dispatch(navigateToView({ view: "dashboard" }));
    });
  };

  const handleLogout = async () => {
    if (user?.id) {
      try {
        await logoutByUserId(user.id);
      } catch (e) {
        console.warn("Session logout API failed", e);
      }
    }
    // Clear session_id will be handled by logout() action
    dispatch(logout());
    dispatch(clearHistory());
    dispatch(navigateToView({ view: "dashboard" }));
  };

  const handleBranchChange = (branch: Branch) => {
    // Update user branch in state and encrypted storage
    dispatch(
      updateUserBranch({
        branch_id: branch.id,
        branch_name: branch.name,
      })
    );

    // Update current branch in layout
    dispatch(setCurrentBranch(branch));

    // Reload the page to refresh all data with new branch context
    setTimeout(() => {
      window.location.reload();
    }, 100);
  };

  const handleMenuToggle = () => {
    dispatch(toggleSideMenu());
  };

  const handleMenuItemClick = (action: string) => {
    dispatch(executeConfigAction(action));
  };

  const handleChatbotNavigate = useMemo(
    () => (view: string) => {
      dispatch(navigateToView({ view, rememberPrevious: true }));
    },
    [dispatch]
  );

  const handleConfigAction = (actionId: string, data?: any) => {
    dispatch(executeConfigAction(actionId, data));
  };

  const renderScreenView = (screenId: string) => {
    return (
      <ScreenRenderer
        screenId={screenId}
        onActionTrigger={handleConfigAction}
        refreshKey={navigation.refreshTimestamp}
      />
    );
  };

  const renderCurrentView = () => {
    if (currentView === "dashboard" || currentView === "home") {
      return <Dashboard />;
    }

    if (currentView === "forms") {
      return <FormDemo />;
    }

    if (currentView === "components") {
      return <Components />;
    }

    if (currentView === "reports") {
      // Use config-driven approach for reports screen
      const reportsScreenId = "167";
      return renderScreenView(reportsScreenId);
    }

    const derivedScreenId = deriveScreenId(currentView);
    if (derivedScreenId) {
      return renderScreenView(derivedScreenId);
    }

    return (
      <div className="p-6 text-center text-gray-500 dark:text-gray-400">
        <h2 className="text-2xl mb-4 text-gray-800 dark:text-gray-100 capitalize">
          {currentView.replace("_", " & ")} Page
        </h2>
        <p className="text-base">
          This page is under construction. Coming soon!
        </p>
      </div>
    );
  };

  const getMenuWidth = () => {
    if (layout.isMobile) return "280px";
    if (layout.isTablet) return "260px";
    return "200px";
  };

  if (!isAuthenticated) {
    return <Login onLogin={handleLogin} />;
  }

  return (
    <div className="bg-gray-100 dark:bg-gray-900 h-screen overflow-y-auto transition-colors duration-200">
      <Header
        title="Global Components"
        subtitle="Configuration-Driven UI System"
        showMenuButton={
          layout.isMobile || layout.isTablet || !layout.isSideMenuOpen
        }
        onMenuClick={handleMenuToggle}
        onLogout={handleLogout}
        userInfo={user}
        currentBranch={layout.currentBranch}
        branches={layout.availableBranches}
        onBranchChange={handleBranchChange}
        showChatbot={currentView !== "dashboard" && currentView !== "home"}
        onChatbotNavigate={handleChatbotNavigate}
        onNotificationNavigate={handleChatbotNavigate}
        onWhatsNewNavigate={handleChatbotNavigate}
        showNewUpdatesNotification={showNewUpdatesNotification}
        showSystemNotifications={showSystemNotifications}
        onLogoClick={() => dispatch(navigateToView({ view: "dashboard" }))}
      />

      <SideMenu
        isOpen={layout.isSideMenuOpen}
        onToggle={handleMenuToggle}
        onMenuItemClick={handleMenuItemClick}
        activeView={navigation.currentView}
        drawerMenuConfig={drawerMenuConfig}
      />

      <div
        className="flex-1 mt-16 transition-[margin-left] duration-300 ease-[cubic-bezier(0.4,0,0.2,1)] h-[calc(100vh-4rem)] overflow-y-auto"
        style={{
          marginLeft:
            !layout.isMobile && !layout.isTablet && layout.isSideMenuOpen
              ? getMenuWidth()
              : "0",
        }}
      >
        {renderCurrentView()}
      </div>

      {notification && (
        <div
          className={`fixed top-20 right-5 z-[10000] min-w-[300px] max-w-[500px] p-4 px-5 rounded-lg shadow-lg flex items-center justify-between gap-3 text-white font-sans animate-slide-in ${notification.type === "success"
            ? "bg-primary-green"
            : notification.type === "error"
              ? "bg-danger-500"
              : "bg-blue-500"
            }`}
        >
          <div className="flex items-center gap-3 flex-1">
            {notification.type === "success" && (
              <span className="text-xl">✓</span>
            )}
            {notification.type === "error" && (
              <span className="text-xl">✕</span>
            )}
            {notification.type === "info" && <span className="text-xl">ℹ</span>}
            <span className="text-sm font-medium">{notification.message}</span>
          </div>
          <button
            onClick={() => dispatch(clearNotification())}
            className="bg-transparent border-none text-white cursor-pointer text-lg p-0 w-6 h-6 flex items-center justify-center opacity-80 hover:opacity-100 transition-opacity"
          >
            ×
          </button>
        </div>
      )}

      <ScreenPopupModal />

      {/* PDF Viewer Modal */}
      <PDFViewer />

      {/* HTML Drawer */}
      <HTMLDrawer />

      {/* Confirmation Dialog */}
      <ConfirmationDialog />

      {/* Chatbot - Only show on home/dashboard page */}
      {(currentView === "dashboard" || currentView === "home") && (
        <div data-tour="chatbot">
          <Chatbot onNavigate={handleChatbotNavigate} />
        </div>
      )}

      {/* App Tour */}
      <AppTour />
    </div>
  );
};

export default App;
